//- Pure JavaScript debouncer for Redux actions

class Debouncer {
    constructor() {
      this.timeouts = new Map();
    }
  
    /**
     * Debounce a function call
     * @param {string} key - Unique identifier for this debounced operation
     * @param {Function} fn - Function to debounce
     * @param {number} delay - Delay in milliseconds
     * @param {...any} args - Arguments to pass to the function
     */
    debounce(key, fn, delay, ...args) {
      // Clear existing timeout for this key
      if (this.timeouts.has(key)) {
        clearTimeout(this.timeouts.get(key));
      }
  
      // Set new timeout
      const timeoutId = setTimeout(() => {
        fn(...args);
        this.timeouts.delete(key);
      }, delay);
  
      this.timeouts.set(key, timeoutId);
    }
  
    /**
     * Cancel a debounced operation
     * @param {string} key - Unique identifier for the operation to cancel
     */
    cancel(key) {
      if (this.timeouts.has(key)) {
        clearTimeout(this.timeouts.get(key));
        this.timeouts.delete(key);
        return true;
      }
      return false;
    }
  
    /**
     * Cancel all debounced operations
     */
    cancelAll() {
      this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
      this.timeouts.clear();
    }
  
    /**
     * Check if a debounced operation is pending
     * @param {string} key - Unique identifier for the operation
     * @returns {boolean}
     */
    isPending(key) {
      return this.timeouts.has(key);
    }
  
    /**
     * Get all pending operation keys
     * @returns {string[]}
     */
    getPendingKeys() {
      return Array.from(this.timeouts.keys());
    }
  }
  
  // Create a singleton instance
  const debouncer = new Debouncer();
  
  // Helper function to create debounced dispatch functions
  export const createDebouncedDispatch = (dispatch, key, delay = 300) => {
    return (action, ...args) => {
      debouncer.debounce(key, () => {
        if (typeof action === 'function') {
          // Handle thunk actions
          dispatch(action(...args));
        } else {
          // Handle plain action objects
          dispatch(action);
        }
      }, delay);
    };
  };
  
  // Export the debouncer instance and class
  export { debouncer, Debouncer };
  
  // Usage examples:
  
  // Example 1: Simple debounced dispatch
  /*
  import { debouncer } from './debouncer';
  
  const dispatch = useDispatch();
  
  const handleSearch = (searchValue) => {
    debouncer.debounce('search', () => {
      dispatch(setSearchString(searchValue));
    }, 300);
  };
  */
  
  // Example 2: Using createDebouncedDispatch helper
  /*
  import { createDebouncedDispatch } from './debouncer';
  
  const dispatch = useDispatch();
  const debouncedDispatch = createDebouncedDispatch(dispatch, 'search', 300);
  
  const handleSearch = (searchValue) => {
    debouncedDispatch(setSearchString(searchValue));
  };
  */
  
  // Example 3: Multiple debounced operations
  /*
  import { debouncer } from './debouncer';
  
  const dispatch = useDispatch();
  
  const handleSearch = (searchValue) => {
    debouncer.debounce('search', () => {
      dispatch(setSearchString(searchValue));
    }, 300);
  };
  
  const handleFilter = (filterValue) => {
    debouncer.debounce('filter', () => {
      dispatch(setFilter(filterValue));
    }, 100);
  };
  
  // Cancel search debouncing if needed
  const cancelSearch = () => {
    debouncer.cancel('search');
  };
  */
  
  // Example 4: Integration with cleanup (useEffect)
  /*
  import { useEffect } from 'react';
  import { debouncer } from './debouncer';
  
  function MyComponent() {
    const dispatch = useDispatch();
  
    useEffect(() => {
      // Cleanup on unmount
      return () => {
        debouncer.cancel('search');
        // or debouncer.cancelAll() to cancel all operations
      };
    }, []);
  
    const handleSearch = (searchValue) => {
      debouncer.debounce('search', () => {
        dispatch(setSearchString(searchValue));
      }, 300);
    };
  
    return (
      <input onChange={(e) => handleSearch(e.target.value)} />
    );
  }
  */
  
  // Example 5: Advanced usage with custom keys
  /*
  import { debouncer } from './debouncer';
  
  const dispatch = useDispatch();
  
  // Different debouncing for different users/contexts
  const handleUserSearch = (userId, searchValue) => {
    debouncer.debounce(`search_${userId}`, () => {
      dispatch(setUserSearchString(userId, searchValue));
    }, 300);
  };
  
  // Check if operation is pending
  const isSearchPending = (userId) => {
    return debouncer.isPending(`search_${userId}`);
  };
  */